import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer, Loader2, Swords, MapPin, LayoutGrid, RefreshCw, Pencil, GripVertical } from "lucide-react";
import { labelScheduleMode } from "@/lib/clubwars";
import api from "@/lib/api";

export default function CwSchedule() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sch, setSch] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // {lmId, side, index}
  const [dropTarget, setDropTarget] = useState(null); // `${court}:${slot}`
  const dragId = useRef(null);

  const load = useCallback(() => {
    api.get(`/club-wars/${id}/schedule`).then(({ data }) => { setSch(data); setErr(false); })
      .catch((e) => { if ([401, 403, 404].includes(e.response?.status)) setErr(true); });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const regenerate = () => {
    if (sch?.manual && !window.confirm("Auto-regenerate will overwrite all your manual edits (moved matches & swapped players). Continue?")) return;
    setBusy(true);
    api.post(`/club-wars/${id}/schedule/assign`).then(({ data }) => { setSch(data); toast.success("Schedule regenerated"); })
      .catch(() => toast.error("Could not regenerate")).finally(() => setBusy(false));
  };

  const move = (court, slot) => {
    const lm_id = dragId.current;
    dragId.current = null; setDropTarget(null);
    if (!lm_id) return;
    api.post(`/club-wars/${id}/schedule/move`, { lm_id, court, slot }).then(({ data }) => setSch(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not move match"));
  };

  const swap = (lmId, side, index, player_id) => {
    setEditing(null);
    api.post(`/club-wars/${id}/schedule/swap-player`, { lm_id: lmId, side, index, player_id }).then(({ data }) => { setSch(data); toast.success("Player updated"); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not update player"));
  };

  if (err) return <Center>Could not load the schedule.</Center>;
  if (!sch) return <Center><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></Center>;

  const cols = sch.court_columns || [];
  const rosters = sch.rosters || {};

  return (
    <div className="min-h-screen cw-print-root" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <style>{`@media print { .no-print { display: none !important; } .cw-print-root { background: #fff !important; color: #000 !important; } .cw-print-card { break-inside: avoid; border: 1px solid #ccc !important; background: #fff !important; } .cw-print-title { color: #000 !important; } }`}</style>
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex items-center flex-wrap justify-between gap-3 mb-6 no-print">
          <button data-testid="cwsch-back" onClick={() => nav(`/club-wars/${id}`)} className="text-sm flex items-center gap-2" style={{ color: "var(--text-2)" }}>
            <ArrowLeft className="w-4 h-4" /> Back to event
          </button>
          <div className="flex items-center gap-2">
            <button data-testid="cwsch-regenerate" onClick={regenerate} disabled={busy} className="rounded-full px-4 py-2.5 text-sm font-bold flex items-center gap-2 glass disabled:opacity-50" style={{ color: "var(--text)" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Auto-regenerate
            </button>
            <button data-testid="cwsch-print" onClick={() => window.print()} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2">
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
        </div>

        <div className="mb-4">
          <h1 className="font-display font-black text-3xl flex items-center gap-3 cw-print-title min-w-0"><Swords className="w-7 h-7 text-primary-c shrink-0" /> <span className="min-w-0 break-words">{sch.name}</span></h1>
          <div className="flex flex-wrap gap-4 mt-2 text-sm" style={{ color: "var(--text-2)" }}>
            {sch.venue && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{sch.venue}</span>}
            <span className="flex items-center gap-1"><LayoutGrid className="w-4 h-4" />{sch.courts} courts</span>
            <span>Order: {labelScheduleMode(sch.mode)}</span>
            <span>{sch.total} matches</span>
            {sch.manual && <span className="rounded-full px-2 py-0.5 text-xs font-bold" style={{ background: "rgba(6,182,212,0.15)", color: "var(--primary-ink)" }} data-testid="cwsch-manual-badge">Manually edited</span>}
          </div>
          <p className="text-xs mt-2 no-print" style={{ color: "var(--text-2)" }}>Drag a match by its handle to move it to another court or round. Click the pencil on a player to swap them. No team plays two rounds in a row.</p>
        </div>

        {sch.total === 0 ? (
          <div className="rounded-2xl glow-border p-10 text-center text-sm cw-print-card" style={{ background: "var(--surface)", color: "var(--text-2)" }} data-testid="cwsch-empty">
            No scheduled matches yet. Line-ups must be locked by both captains before a match appears here.
          </div>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(cols.length || 1, 3)}, minmax(0, 1fr))` }} data-testid="cwsch-columns">
            {cols.map((col) => (
              <div key={col.court} className="rounded-2xl glow-border overflow-hidden cw-print-card" style={{ background: "var(--surface)" }} data-testid={`cwsch-court-${col.court}`}>
                <div className="px-4 py-3 text-center font-display font-black text-lg cw-print-title" style={{ background: "var(--primary)", color: "var(--on-primary)" }}>COURT {col.court}</div>
                <div className="px-4 py-2 text-center text-xs font-bold uppercase tracking-wider cw-print-title" style={{ background: "var(--bg)", color: "var(--text-2)" }}>{col.title || "—"}</div>
                <div className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {col.matches.map((m, i) => {
                    const dt = `${col.court}:${i}`;
                    const isTarget = dropTarget === dt;
                    const common = {
                      onDragOver: (e) => { e.preventDefault(); setDropTarget(dt); },
                      onDragLeave: () => setDropTarget((p) => (p === dt ? null : p)),
                      onDrop: () => move(col.court, i),
                      style: { background: isTarget ? "rgba(6,182,212,0.12)" : undefined, transition: "background-color 150ms ease" },
                    };
                    return m ? (
                      <div key={m.lm_id} {...common} draggable onDragStart={() => { dragId.current = m.lm_id; }}
                        className="grid grid-cols-[auto_auto_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-2 py-2.5 text-sm cw-print-title" data-testid={`cwsch-match-${m.lm_id}`}>
                        <GripVertical className="w-3.5 h-3.5 shrink-0 cursor-grab no-print" style={{ color: "var(--text-2)" }} data-testid={`cwsch-drag-${m.lm_id}`} />
                        <span className="mono text-[10px] rounded px-1.5 py-0.5 shrink-0" style={{ background: "var(--bg)", color: "var(--text-2)" }} title={`Round ${i + 1}`}>R{i + 1}</span>
                        <PlayerSide m={m} side="a" rosters={rosters} editing={editing} setEditing={setEditing} onSwap={swap} align="right" />
                        <span className="mono text-[10px]" style={{ color: "var(--text-2)" }}>vs</span>
                        <PlayerSide m={m} side="b" rosters={rosters} editing={editing} setEditing={setEditing} onSwap={swap} align="left" />
                      </div>
                    ) : (
                      <div key={`rest-${dt}`} {...common}
                        className="grid grid-cols-[auto_1fr] items-center gap-2 px-3 py-2.5 text-xs cw-print-title" data-testid={`cwsch-rest-${col.court}-${i}`}>
                        <span className="mono text-[10px] rounded px-1.5 py-0.5 shrink-0" style={{ background: "var(--bg)", color: "var(--text-2)" }}>R{i + 1}</span>
                        <span style={{ color: "var(--text-2)", opacity: 0.6 }}>{isTarget ? "drop here" : "— rest —"}</span>
                      </div>
                    );
                  })}
                  <div onDragOver={(e) => { e.preventDefault(); setDropTarget(`${col.court}:end`); }}
                    onDragLeave={() => setDropTarget((p) => (p === `${col.court}:end` ? null : p))}
                    onDrop={() => move(col.court, col.matches.length)}
                    className="px-3 py-2 text-center text-[10px] no-print" data-testid={`cwsch-dropend-${col.court}`}
                    style={{ color: "var(--text-2)", opacity: dropTarget === `${col.court}:end` ? 1 : 0.35, background: dropTarget === `${col.court}:end` ? "rgba(6,182,212,0.12)" : undefined }}>
                    + new round
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PlayerSide({ m, side, rosters, editing, setEditing, onSwap, align }) {
  const pids = side === "a" ? (m.a_pids || []) : (m.b_pids || []);
  const names = side === "a" ? (m.a_players || []) : (m.b_players || []);
  const teamId = side === "a" ? m.team_a_id : m.team_b_id;
  const roster = rosters[teamId] || [];
  const started = m.status === "live" || m.status === "final";
  return (
    <div className={`flex flex-wrap items-center gap-1 ${align === "right" ? "justify-end" : "justify-start"}`}>
      {names.map((nm, idx) => {
        const open = editing && editing.lmId === m.lm_id && editing.side === side && editing.index === idx;
        return (
          <span key={idx} className="relative inline-flex items-center gap-1 font-medium">
            {nm}
            {!started && (
              <button onClick={() => setEditing(open ? null : { lmId: m.lm_id, side, index: idx })}
                className="no-print opacity-50 hover:opacity-100" data-testid={`cwsch-edit-${m.lm_id}-${side}-${idx}`} title="Swap player">
                <Pencil className="w-3 h-3" style={{ color: "var(--primary-ink)" }} />
              </button>
            )}
            {open && (
              <div className="absolute z-20 top-full mt-1 left-0 w-44 max-h-56 overflow-auto rounded-xl glass p-1 text-left" style={{ border: "1px solid var(--border)", background: "var(--surface)" }} data-testid={`cwsch-dropdown-${m.lm_id}-${side}-${idx}`}>
                {roster.filter((p) => !(pids.includes(p.id) && p.id !== pids[idx])).map((p) => (
                  <button key={p.id} onClick={() => onSwap(m.lm_id, side, idx, p.id)}
                    className="block w-full text-left px-2.5 py-1.5 text-sm rounded-lg hover:bg-[var(--bg)]" data-testid={`cwsch-pick-${p.id}`}
                    style={{ color: p.id === pids[idx] ? "var(--primary)" : "var(--text)" }}>
                    {p.name}{p.id === pids[idx] ? " ✓" : ""}
                  </button>
                ))}
                {roster.length === 0 && <div className="px-2.5 py-2 text-xs" style={{ color: "var(--text-2)" }}>No roster players</div>}
              </div>
            )}
            {idx < names.length - 1 && <span style={{ color: "var(--text-2)" }}>&</span>}
          </span>
        );
      })}
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-screen flex items-center justify-center text-sm" style={{ background: "var(--bg)", color: "var(--text-2)" }}>{children}</div>;
}
